// ---- player-painted TERRAIN (sand / dirt / paths) --------------------------
// Terrain is a purely COSMETIC ground overlay painted on the grass with a round brush,
// exactly like water but layered UNDER the water and soil. It reuses the water sub-grid
// (WATER_CELL keys "wx,wy") so a single brush machinery, plot-clipping and depth model
// serve both. Each painted cell stores ONE terrain type; painting a new type over a cell
// replaces it; the eraser removes the cell (grass shows through again).
//
// Unlike water, terrain does NOT block plants or collide with anything — it's just a
// re-skin of the ground. It also may sit under plants/decor (a path can run beneath a
// flowerbed). It IS clipped to the plot and never painted onto a water cell (water wins).

import {
  WATER_CELL, cellInPlot, waterCellAt, waterKey, waterCellCenter, packCells, unpackCells, MAX_UNPACKED_CELLS,
} from "./garden";
import type { Plot } from "../render/plot";

/** The paintable terrain types (plus "erase", handled by the caller as removal). */
export type TerrainType = "sand" | "dirt" | "pebble" | "stone";
export const TERRAIN_TYPES: readonly TerrainType[] = ["sand", "dirt", "pebble", "stone"] as const;

/** Display metadata for the tool palette. `path` types render a laid patterned walkway;
 *  `fill` types render a solid natural ground (sand/dirt). */
export interface TerrainDef {
  id: TerrainType;
  label: string;
  /** short glyph shown on the palette chip */
  glyph: string;
  /** dominant swatch colour (for the palette chip + a quick tint) */
  swatch: string;
  kind: "fill" | "path";
}

export const TERRAIN_DEFS: Record<TerrainType, TerrainDef> = {
  sand:   { id: "sand",   label: "Sand",        glyph: "◦", swatch: "#655d4a", kind: "fill" },
  dirt:   { id: "dirt",   label: "Dirt",        glyph: "▪", swatch: "#584a38", kind: "fill" },
  pebble: { id: "pebble", label: "Pebble Path", glyph: "∴", swatch: "#a9a29a", kind: "path" },
  stone:  { id: "stone",  label: "Stone Path",  glyph: "▤", swatch: "#9a9ba0", kind: "path" },
};

/** Default terrain brush radius (garden units) — a touch tighter than the water brush so
 *  paths can be drawn narrow. Scroll-resizable in the UI, reverting to this on tool change. */
export const TERRAIN_BRUSH_R = 1.0;
export const TERRAIN_BRUSH_MIN = 0.5;
export const TERRAIN_BRUSH_MAX = 5.0;
export const TERRAIN_BRUSH_STEP = 0.3;

// ---- STONE PATH: a DIFFERENT brush model ----------------------------------------------------------
// The stone path lays discrete square PAVERS, so its brush is an iso DIAMOND snapped to the paver grid
// (each paver = ONE water sub-grid cell) and sized in WHOLE-PAVER increments — you always place a clean
// N×N block of tiles, never a ragged round edge. `TERRAIN_STONE_PAVERS_*` count pavers PER SIDE of that
// square block (so 3 → a 3×3 diamond of tiles).
export const TERRAIN_STONE_PAVERS = 3;
export const TERRAIN_STONE_PAVERS_MIN = 1;
export const TERRAIN_STONE_PAVERS_MAX = 11;

// ---- ERASER SHAPE ---------------------------------------------------------------------------------
// The eraser can clear either with the natural types' round brush (the default — unchanged muscle memory)
// or with a SQUARE block of whole sub-grid cells, which is the shape a stone path is actually made of.
// Clearing a paver run with the round brush leaves crescent slivers behind at every pass; the square
// eraser lines up with the same cells `stoneBlockCells` writes, so a path comes up in whole tiles.
export type TerrainEraseShape = "round" | "square";
export const TERRAIN_ERASE_SHAPE: TerrainEraseShape = "round";
// Square eraser size, in whole cells PER SIDE — the same units (and the same block helper) as the stone
// path's pavers, so "3" erases exactly what "3×3" laid.
export const TERRAIN_ERASE_CELLS = 3;
export const TERRAIN_ERASE_CELLS_MIN = 1;
export const TERRAIN_ERASE_CELLS_MAX = 11;

/** The cell whose box contains (gx,gy), and its snapped garden centre — the paver the cursor is over. */
export function paverAt(gx: number, gy: number): { wx: number; wy: number; cx: number; cy: number } {
  const { wx, wy } = waterCellAt(gx, gy);
  const cc = waterCellCenter(wx, wy);
  return { wx, wy, cx: cc.gx, cy: cc.gy };
}

/**
 * The cells of an N×N block of PAVERS (a diamond on screen) centred on the paver under (gx,gy). `pavers`
 * is the side length in tiles (odd → cleanly centred; even → biased +). Clipped to the plot + off water
 * (for painting). Used by the STONE path so the brush lays exact whole tiles — and by the SQUARE ERASER,
 * which must clear the identical footprint or a path never comes up cleanly.
 */
export function stoneBlockCells(
  plot: Plot, gx: number, gy: number, pavers: number,
  water: ReadonlySet<string>, forErase = false,
): string[] {
  const n = Math.max(1, Math.round(pavers));
  const { wx: cwx, wy: cwy } = waterCellAt(gx, gy);
  const lo = -Math.floor((n - 1) / 2), hi = Math.ceil((n - 1) / 2);
  const out: string[] = [];
  for (let dx = lo; dx <= hi; dx++) {
    for (let dy = lo; dy <= hi; dy++) {
      const wx = cwx + dx, wy = cwy + dy;
      const key = waterKey(wx, wy);
      if (!forErase) {
        if (!cellInPlot(plot, wx, wy)) continue;
        if (water.has(key)) continue;
      }
      out.push(key);
    }
  }
  return out;
}

/**
 * The terrain cells a round BRUSH (radius in garden units) covers around (gx, gy), clipped
 * to the plot and (for painting) never onto a water cell. Returns the cell keys to SET to
 * `type` (or to DELETE when `forErase`). Terrain may sit under plants/decor, so — unlike
 * water — there is no plant/decor exclusion.
 */
export function brushTerrainCells(
  plot: Plot, gx: number, gy: number, radius: number,
  water: ReadonlySet<string>, forErase = false,
): string[] {
  const out: string[] = [];
  const r = Math.max(WATER_CELL * 0.5, radius);
  const c0 = waterCellAt(gx - r, gy - r), c1 = waterCellAt(gx + r, gy + r);
  for (let wx = c0.wx; wx <= c1.wx; wx++) {
    for (let wy = c0.wy; wy <= c1.wy; wy++) {
      const cc = waterCellCenter(wx, wy);
      if ((cc.gx - gx) ** 2 + (cc.gy - gy) ** 2 > r * r) continue;   // round brush
      const key = waterKey(wx, wy);
      if (!forErase) {
        if (!cellInPlot(plot, wx, wy)) continue;      // stay on the green
        if (water.has(key)) continue;                 // water wins — never paint terrain on a pond
      }
      out.push(key);
    }
  }
  return out;
}

/** Drop any terrain cell that has since become water, or fallen off-plot (old saves / plot
 *  shrank) — so the rendered terrain never collides with water or spills off the green. */
export function effectiveTerrain(
  terrain: ReadonlyMap<string, TerrainType>, water: ReadonlySet<string>, plot?: Plot,
): Map<string, TerrainType> {
  const out = new Map<string, TerrainType>();
  for (const [key, t] of terrain) {
    if (water.has(key)) continue;
    if (plot) {
      const [wx, wy] = key.split(",").map(Number) as [number, number];
      if (!cellInPlot(plot, wx, wy)) continue;
    }
    out.set(key, t);
  }
  return out;
}

/** Is (gx,gy) on a painted terrain cell? Returns its type or null. */
export function terrainAt(terrain: ReadonlyMap<string, TerrainType>, gx: number, gy: number): TerrainType | null {
  const { wx, wy } = waterCellAt(gx, gy);
  return terrain.get(waterKey(wx, wy)) ?? null;
}

/** Persisted form. In memory (and in saves written before packing) a plain map of cellKey → type; on
 *  the wire, `packTerrain`'s string. Defensive coerce of either. */
export function coerceTerrain(raw: unknown): Record<string, TerrainType> {
  const out: Record<string, TerrainType> = {};
  if (typeof raw === "string") return unpackTerrain(raw);
  if (!raw || typeof raw !== "object") return out;
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof k !== "string" || !/^-?\d+,-?\d+$/.test(k)) continue;
    if (typeof v === "string" && (TERRAIN_TYPES as readonly string[]).includes(v)) out[k] = v as TerrainType;
  }
  return out;
}

/** The wire form: one row-run string per type (`packCells`, sim/garden.ts), `"sand=…|stone=…"`.
 *  A per-key record costs ~22 bytes a cell; this costs a few bytes a ROW. Empty types are omitted. */
export function packTerrain(r: Record<string, TerrainType>): string {
  const byType = new Map<TerrainType, string[]>();
  for (const [k, t] of Object.entries(r)) {
    let ks = byType.get(t);
    if (!ks) byType.set(t, (ks = []));
    ks.push(k);
  }
  return TERRAIN_TYPES.filter((t) => byType.has(t)).map((t) => `${t}=${packCells(byType.get(t)!)}`).join("|");
}
function unpackTerrain(packed: string): Record<string, TerrainType> {
  const out: Record<string, TerrainType> = {};
  let n = 0;
  for (const part of packed.split("|")) {
    const eq = part.indexOf("=");
    const t = part.slice(0, eq) as TerrainType;
    if (eq < 0 || !TERRAIN_TYPES.includes(t)) continue;
    for (const k of unpackCells(part.slice(eq + 1))) {
      if (n++ >= MAX_UNPACKED_CELLS) return out;   // the cap is per SAVE, not per type
      out[k] = t;
    }
  }
  return out;
}

/** Map ⇄ record helpers for save/load. */
export function terrainToRecord(m: ReadonlyMap<string, TerrainType>): Record<string, TerrainType> {
  const out: Record<string, TerrainType> = {};
  for (const [k, v] of m) out[k] = v;
  return out;
}
export function recordToTerrain(r: Record<string, TerrainType> | undefined): Map<string, TerrainType> {
  const m = new Map<string, TerrainType>();
  if (r) for (const [k, v] of Object.entries(r)) m.set(k, v);
  return m;
}
